import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Shield,
  // Bell,
  Edit,
  Upload,
  ChevronRight,
  Phone,
  MapPin,
  Mail,
  CheckCircle2,
  Eye,
  EyeOff,
  ArrowLeft,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import settingsService from "../../api/SettingsService";
import { clearAuth, setUser } from "../../store/slices/authSlice";
import { formatGhanaPhoneNumber } from "../../utils/formValidators";

const capitalizeCountryForPayload = (country) => {
  if (!country) return "";
  const normalized = String(country).trim().toLowerCase();
  if (!normalized) return "";

  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isGhanaCountry = (country) => {
  if (!country) return false;
  return String(country).trim().toLowerCase() === "ghana";
};

const normalizePhoneForPayload = (phone) => {
  if (!phone) return "";
  return String(phone).replace(/^\+/, "").replace(/\D/g, "");
};

const Settings = () => {
  const calculateAgeFromDOB = (dobString) => {
    if (!dobString || dobString.length !== 8) return null;

    // Parse DOB in format DDMMYYYY (e.g., "18091996")
    const day = parseInt(dobString.substring(0, 2));
    const month = parseInt(dobString.substring(2, 4)) - 1;
    const year = parseInt(dobString.substring(4, 8));

    const birthDate = new Date(year, month, day);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const formatDOBForDateInput = (dobString) => {
    if (!dobString || dobString.length !== 8) return "";

    // Convert DDMMYYYY to YYYY-MM-DD for date input
    const day = dobString.substring(0, 2);
    const month = dobString.substring(2, 4);
    const year = dobString.substring(4, 8);

    return `${year}-${month}-${day}`;
  };

  const formatDateInputToDOB = (dateString) => {
    if (!dateString) return "";

    // Convert YYYY-MM-DD to DDMMYYYY
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());

    return `${day}${month}${year}`;
  };

  // Function to format Ghana Card number as user types (GHA-XXXXXXXXX-X)
  const formatGhanaCardNumber = (value) => {
    if (!value) return value;

    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "");

    // If it starts with GHA, format it
    if (cleaned.toUpperCase().startsWith("GHA")) {
      const ghaPart = cleaned.substring(0, 3).toUpperCase();
      const numbers = cleaned.substring(3);

      if (numbers.length <= 10) {
        if (numbers.length <= 9) {
          return `${ghaPart}-${numbers}`;
        } else if (numbers.length === 10) {
          return `${ghaPart}-${numbers.substring(0, 9)}-${numbers.substring(9)}`;
        }
      }
    }

    // If it doesn't start with GHA, just return the cleaned value
    return cleaned;
  };


  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [activeTab, setActiveTab] = useState("profile");

  const formatPhoneForPayloadByCountry = (phone, country) => {
    if (!phone) return "";
    // Backend expects digits-only; for Ghana we also normalize 0/233 variants.
    if (isGhanaCountry(country)) return formatGhanaPhoneNumber(phone);
    return normalizePhoneForPayload(phone);
  };

  const [profileData, setProfileData] = useState({
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    email: user?.email || "",
    phone: formatPhoneForPayloadByCountry(
      user?.phone_number || "",
      user?.country || ""
    ),
    country: user?.country || "",
    dob: formatDOBForDateInput(user?.dob),
    age: calculateAgeFromDOB(user?.dob),
    location: user?.location || "",
    region: user?.region || "",
    gender: user?.gender || "",
    document_type: user?.document_type || "",
    card_number: user?.card_number || "",
    issue_date: user?.issue_date ? new Date(user.issue_date).toISOString().split('T')[0] : "",
    expiry_date: user?.expiry_date ? new Date(user.expiry_date).toISOString().split('T')[0] : "",
    id_card_front: user?.id_card_front || "",
    id_card_back: user?.id_card_back || "",
  });

  const isGhana = isGhanaCountry(profileData.country);

  const phoneDisplayValue = (() => {
    if (!profileData.phone) return "";
    // `profileData.phone` is already normalized for the payload in state init.
    // Since backend includes the country code, don't re-apply Ghana/international formatting here.
    return isGhana ? profileData.phone : `+${profileData.phone}`;
  })();

  const [securityData, setSecurityData] = useState({
    old_password: "",
    password: "",
    password_confirmation: "",
  });

  const [notificationPrefs, setNotificationPrefs] = useState({
    financialOffers: "yes",
    promotionalDeals: "yes",
    method: "email",
  });

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [documents, setDocuments] = useState({
    ghanaCardFront: { uploaded: true, date: "27/03/2021" },
    ghanaCardBack: { uploaded: true, date: "27/03/2030" },
  });

  // File inputs refs for programmatic click
  const fileInputs = useRef({});

  // Format card number for display when component loads or user changes
  useEffect(() => {
    const docType = user?.document_type;
    if (user?.card_number && (docType === "Ghana Card" || docType === "ghana_card")) {
      const cardNum = user.card_number;
      // If it's stored without dashes, format it for display
      if (cardNum && cardNum.toUpperCase().startsWith("GHA") && !cardNum.includes("-") && cardNum.length === 13) {
        setProfileData((prev) => ({
          ...prev,
          card_number: formatGhanaCardNumber(cardNum),
        }));
      }
    }
  }, [user?.card_number, user?.document_type]);

  const menuItems = [
    { id: "profile", label: "Profile", icon: User },
    { id: "security", label: "Security", icon: Shield },
    // { id: 'notification', label: 'Notification', icon: Bell }
  ];

  const cardMotion = {
    initial: { opacity: 0, y: 12, scale: 0.99 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.4, ease: "easeOut" },
    viewport: { once: true, amount: 0.2 },
  };

  // Shared input style
  const inputClass =
    "px-3 py-3 w-full bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-inner placeholder:text-gray-400";

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "gray" };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    score += checks.length ? 1 : 0;
    score += checks.lowercase ? 1 : 0;
    score += checks.uppercase ? 1 : 0;
    score += checks.numbers ? 1 : 0;
    score += checks.symbols ? 1 : 0;

    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;

    let label, color;
    if (score <= 2) {
      label = "Very Weak";
      color = "red";
    } else if (score <= 3) {
      label = "Weak";
      color = "orange";
    } else if (score <= 4) {
      label = "Fair";
      color = "yellow";
    } else if (score <= 5) {
      label = "Good";
      color = "lime";
    } else if (score <= 6) {
      label = "Strong";
      color = "green";
    } else {
      label = "Very Strong";
      color = "emerald";
    }

    return { score, label, color, checks };
  };

  const handleProfileUpdate = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecurityUpdate = (field, value) => {
    setSecurityData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordUpdate = async () => {
    // Validate passwords match
    if (securityData.password !== securityData.password_confirmation) {
      toast.error("New passwords don't match");
      return;
    }

    // Validate password strength (same as signup)
    const password = securityData.password;
    const hasLength = password.length >= 8;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const criteriaMet = [
      hasLength,
      hasLowercase,
      hasUppercase,
      hasNumbers,
      hasSymbols,
    ].filter(Boolean).length;

    if (criteriaMet < 4) {
      toast.error(
        "Password must meet at least 4 of the 5 criteria: length ≥8, lowercase, uppercase, numbers, symbols"
      );
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await settingsService.changePassword(securityData);

      if (response.status) {
        toast.success(response.message);
        // Clear the form
        setSecurityData({
          old_password: "",
          password: "",
          password_confirmation: "",
        });
        // Logout the user immediately
        dispatch(clearAuth());
        window.location.href = "/signin";
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while changing password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleNotificationUpdate = (field, value) => {
    setNotificationPrefs((prev) => ({ ...prev, [field]: value }));
  };

  const handleDocumentUpload = (docType) => {
    if (fileInputs.current[docType]) {
      fileInputs.current[docType].click();
    }
  };

  const convertImgToBase64 = (file, field) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result; // This includes "data:image/..." prefix
        setProfileData((prev) => ({ ...prev, [field]: base64String }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error converting image to base64:", error);
      toast.error("Error uploading image. Please try again.");
      return null;
    }
  };

  const handleFileChange = (docType, event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    // Convert to base64 and update profileData
    if (docType === "ghanaCardFront") {
      convertImgToBase64(file, "id_card_front");
    } else if (docType === "ghanaCardBack") {
      convertImgToBase64(file, "id_card_back");
    }

    // Update documents state for preview
    const updated = {
      uploaded: true,
      date: new Date().toLocaleDateString(),
      name: file.name,
      previewUrl: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
    };
    setDocuments((prev) => ({
      ...prev,
      [docType]: { ...(prev[docType] || {}), ...updated },
    }));
  };

  const handleProfileSave = async () => {
    setIsSavingProfile(true);
    try {
      // Prepare the payload for the API
      // Map document_type from display name to API format if needed
      const mapDocumentType = (docType) => {
        if (!docType) return "";
        const mapping = {
          "Ghana Card": "ghana_card",
          "Passport": "passport",
          "Drivers License": "drivers_license",
        };
        // If it's already in API format, return as is; otherwise map it
        return mapping[docType] || docType;
      };

      // Format card_number for Ghana Card (remove dashes to get original format)
      const formatCardNumber = (cardNumber, documentType) => {
        if (!cardNumber) return "";
        // If it's Ghana Card, remove dashes to get original format (GHA1234567890)
        if (documentType === "Ghana Card" || documentType === "ghana_card") {
          return cardNumber.replace(/-/g, "");
        }
        return cardNumber;
      };

      const payload = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        email: profileData.email || user?.email || "", // Include email (required by API)
        phone_number: formatPhoneForPayloadByCountry(
          profileData.phone || user?.phone_number || "",
          profileData.country || user?.country || ""
        ), // Include phone_number (required by API)
        password: "", // Include password field (required by API, but empty since we're not changing it)
        gender: profileData.gender ? profileData.gender.toLowerCase() : "",
        dob: formatDateInputToDOB(profileData.dob),
        country: capitalizeCountryForPayload(
          profileData.country || user?.country
        ),
        location: profileData.location,
        region: profileData.region,
        // Document-related fields (optional - only include if they have values)
        ...(profileData.document_type && { 
          document_type: mapDocumentType(profileData.document_type) 
        }),
        ...(profileData.card_number && { 
          card_number: formatCardNumber(profileData.card_number, profileData.document_type) 
        }),
        ...(profileData.issue_date && { issue_date: profileData.issue_date }),
        ...(profileData.expiry_date && { expiry_date: profileData.expiry_date }),
        ...(profileData.id_card_front && { id_card_front: profileData.id_card_front }),
        ...(profileData.id_card_back && { id_card_back: profileData.id_card_back }),
      };

      const response = await settingsService.updateProfile(payload);

      if (response.status) {
        toast.success(response.message);
        const userData = response?.data?.data?.data;
        
        // Update the user data in Redux store
        const updatedUser = {
          ...user,
          first_name: userData.first_name,
          last_name: userData.last_name,
          gender: userData.gender,
          dob: formatDateInputToDOB(userData.dob),
          location: userData.location,
          region: userData.region,
          country: userData.country || user?.country || "",
          // Include document fields if they were updated
          ...(userData.document_type && { document_type: userData.document_type }),
          ...(userData.card_number && { card_number: userData.card_number }),
          ...(userData.issue_date && { issue_date: userData.issue_date }),
          ...(userData.expiry_date && { expiry_date: userData.expiry_date }),
          ...(userData.id_card_front && { id_card_front: userData.id_card_front }),
          ...(userData.id_card_back && { id_card_back: userData.id_card_back }),
          // Keep the original email and phone as they can't be changed
          email: user.email,
          phone_number: formatPhoneForPayloadByCountry(
            user.phone_number,
            profileData.country || user?.country || ""
          ),
        };
        
        dispatch(setUser(updatedUser));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while saving profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // const handleNotificationSave = () => {
  //   // TODO: Integrate API save
  //   console.log("Save notifications:", notificationPrefs);
  // };

  const renderProfileSection = () => (
    <motion.section
      {...cardMotion}
      className="overflow-hidden relative p-6 bg-white rounded-2xl ring-1 ring-gray-100 shadow-xl"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Personal & Contact Information
        </h2>
        <Edit className="w-5 h-5 text-gray-400" />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2">
        <div>
          <label className="block mb-1 text-sm text-gray-600">First Name</label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => handleProfileUpdate("firstName", e.target.value)}
            className={inputClass}
            placeholder="Mark"
          />
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Last Name</label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => handleProfileUpdate("lastName", e.target.value)}
            className={inputClass}
            placeholder="Brou"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block mb-1 text-sm text-gray-600">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="email"
              value={profileData.email}
              readOnly
              className={`pl-10 bg-gray-100 cursor-not-allowed ${inputClass}`}
              placeholder="broumatt544@gmail.com"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Phone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="tel"
              value={phoneDisplayValue}
              readOnly
              className={`pl-10 bg-gray-100 cursor-not-allowed ${inputClass}`}
              placeholder={isGhana ? "0596540242" : "e.g., 2348012345678"}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Phone cannot be changed</p>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Country</label>
          <input
            type="text"
            value={profileData.country}
            onChange={(e) => handleProfileUpdate("country", e.target.value)}
            className={inputClass}
            placeholder="Enter your country"
          />
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Gender</label>
          <select
            value={profileData.gender || ''}
            onChange={(e) => handleProfileUpdate('gender', e.target.value)}
            className={inputClass}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">
            Date of Birth
          </label>
          <input
            type="date"
            value={profileData.dob}
            onChange={(e) => {
              const newDob = formatDateInputToDOB(e.target.value);
              const newAge = calculateAgeFromDOB(newDob);
              handleProfileUpdate("dob", e.target.value);
              handleProfileUpdate("age", newAge);
            }}
            className={inputClass}
          />
          {profileData.age && (
            <p className="mt-1 text-xs text-gray-500">
              Age: {profileData.age} years old
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">
            {isGhana ? "Region" : "State/Province"}
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              value={profileData.region}
              onChange={(e) => handleProfileUpdate("region", e.target.value)}
              className={`pl-10 ${inputClass}`}
              placeholder={isGhana ? "Greater Accra" : "e.g., Lagos"}
            />
          </div>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">
            {isGhana ? "Location" : "City/Town"}
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              value={profileData.location}
              onChange={(e) => handleProfileUpdate("location", e.target.value)}
              className={`pl-10 ${inputClass}`}
              placeholder={isGhana ? "Dzorwulu" : "e.g., Victoria Island"}
            />
          </div>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Document Type</label>
          <select
            value={profileData.document_type || ''}
            onChange={(e) => handleProfileUpdate('document_type', e.target.value)}
            className={inputClass}
          >
            <option value="">Select Document Type</option>
            <option value="ghana_card">Ghana Card</option>
            <option value="passport">Passport</option>
          </select>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Card Number</label>
          <input
            type="text"
            value={profileData.card_number}
            onChange={(e) => {
              const value = e.target.value;
              // Apply Ghana Card formatting if Ghana Card is selected
              if (profileData.document_type === "Ghana Card") {
                const formatted = formatGhanaCardNumber(value);
                handleProfileUpdate("card_number", formatted);
              } else {
                handleProfileUpdate("card_number", value);
              }
            }}
            onPaste={(e) => {
              if (profileData.document_type === "Ghana Card") {
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");
                const formatted = formatGhanaCardNumber(pastedText);
                handleProfileUpdate("card_number", formatted);
              }
            }}
            className={inputClass}
            placeholder={
              profileData.document_type === "Ghana Card"
                ? "GHA-XXXXXXXXX-X"
                : "Enter card number"
            }
            disabled={!profileData.document_type}
          />
          {/* Helper text for Ghana Card format */}
          {profileData.document_type === "Ghana Card" && (
            <motion.p
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="mt-1 text-xs text-gray-500"
            >
              Format: GHA-XXXXXXXXX-X (e.g., GHA-123456789-0)
            </motion.p>
          )}
          {/* Validation message for Ghana Card */}
          {profileData.document_type === "Ghana Card" && profileData.card_number && (
            <motion.p
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              className={`mt-1 text-xs ${
                (() => {
                  const cleanedCardNumber = profileData.card_number.replace(/-/g, "");
                  const ghanaCardPattern = /^GHA\d{10}$/i;
                  return ghanaCardPattern.test(cleanedCardNumber);
                })()
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {(() => {
                const cleanedCardNumber = profileData.card_number.replace(/-/g, "");
                const ghanaCardPattern = /^GHA\d{10}$/i;
                return ghanaCardPattern.test(cleanedCardNumber)
                  ? "✅ Valid Ghana Card format"
                  : "❌ Invalid Ghana Card format";
              })()}
            </motion.p>
          )}
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Issue Date</label>
          <input
            type="date"
            value={profileData.issue_date}
            onChange={(e) => handleProfileUpdate("issue_date", e.target.value)}
            className={inputClass}
            disabled={!profileData.document_type}
          />
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-600">Expiry Date</label>
          <input
            type="date"
            value={profileData.expiry_date}
            onChange={(e) => handleProfileUpdate("expiry_date", e.target.value)}
            className={inputClass}
            disabled={!profileData.document_type}
          />
        </div>
      </div>

      {/* Document Upload Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Document Upload
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <motion.div
            whileHover={{ y: -2 }}
            className="relative p-4 bg-white rounded-xl ring-1 ring-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium text-gray-900">
                GHANA CARD FRONT
              </span>
              {(documents.ghanaCardFront.uploaded || profileData.id_card_front) && (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              )}
            </div>
            <div className="mb-3 text-sm text-gray-600">Ghana Card</div>
            {(documents.ghanaCardFront.previewUrl || profileData.id_card_front) ? (
              <img
                src={documents.ghanaCardFront.previewUrl || profileData.id_card_front}
                alt="Ghana Card Front Preview"
                className="object-contain mb-3 w-full max-h-64 rounded-lg ring-1 ring-gray-100 bg-gray-50"
              />
            ) : null}
            <div className="mb-3 text-sm text-gray-500">
              {documents.ghanaCardFront.date}
              {documents.ghanaCardFront.name ? (
                <span className="ml-2 text-gray-400">
                  • {documents.ghanaCardFront.name}
                </span>
              ) : null}
            </div>
            <button
              onClick={() => handleDocumentUpload("ghanaCardFront")}
              className="flex gap-2 items-center text-emerald-600 transition-colors hover:text-emerald-700"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm">Re-upload</span>
            </button>
            <input
              type="file"
              accept="image/*,application/pdf"
              ref={(el) => (fileInputs.current["ghanaCardFront"] = el)}
              onChange={(e) => handleFileChange("ghanaCardFront", e)}
              className="hidden"
            />
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="relative p-4 bg-white rounded-xl ring-1 ring-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium text-gray-900">GHANA CARD BACK</span>
              {(documents.ghanaCardBack.uploaded || profileData.id_card_back) && (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              )}
            </div>
            <div className="mb-3 text-sm text-gray-600">Card Back</div>
            {(documents.ghanaCardBack.previewUrl || profileData.id_card_back) ? (
              <img
                src={documents.ghanaCardBack.previewUrl || profileData.id_card_back}
                alt="Ghana Card Back Preview"
                className="object-contain mb-3 w-full max-h-64 rounded-lg ring-1 ring-gray-100 bg-gray-50"
              />
            ) : null}
            <div className="mb-3 text-sm text-gray-500">
              {documents.ghanaCardBack.date}
              {documents.ghanaCardBack.name ? (
                <span className="ml-2 text-gray-400">
                  • {documents.ghanaCardBack.name}
                </span>
              ) : null}
            </div>
            <button
              onClick={() => handleDocumentUpload("ghanaCardBack")}
              className="flex gap-2 items-center text-emerald-600 transition-colors hover:text-emerald-700"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm">Re-upload</span>
            </button>
            <input
              type="file"
              accept="image/*,application/pdf"
              ref={(el) => (fileInputs.current["ghanaCardBack"] = el)}
              onChange={(e) => handleFileChange("ghanaCardBack", e)}
              className="hidden"
            />
          </motion.div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-6 mt-8 border-t border-gray-100">
        <button
          onClick={handleProfileSave}
          disabled={isSavingProfile}
          className={`px-6 py-3 font-medium text-white rounded-lg transition-colors ${
            isSavingProfile
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-emerald-500 hover:bg-emerald-600"
          }`}
        >
          {isSavingProfile ? "Saving..." : "Save profile"}
        </button>
      </div>
    </motion.section>
  );

  const renderSecuritySection = () => (
    <motion.section
      {...cardMotion}
      className="overflow-hidden relative p-6 bg-white rounded-2xl ring-1 ring-gray-100 shadow-xl"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Security</h2>
        <Edit className="w-5 h-5 text-gray-400" />
      </div>

      <div className="space-y-6">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Current Password
          </label>
          <input
            type="password"
            placeholder="Enter your current password"
            value={securityData.old_password}
            onChange={(e) =>
              handleSecurityUpdate("old_password", e.target.value)
            }
            className="px-3 py-3 w-full bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        <div>
          <label className="block mb-4 text-sm font-medium text-gray-900">
            New Password
          </label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={securityData.password}
                onChange={(e) =>
                  handleSecurityUpdate("password", e.target.value)
                }
                className="px-3 py-3 pr-10 w-full bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 p-1 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={securityData.password_confirmation}
                onChange={(e) =>
                  handleSecurityUpdate("password_confirmation", e.target.value)
                }
                className="px-3 py-3 pr-10 w-full bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 p-1 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                title={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Password Strength Indicator */}
          {securityData.password && (
            <div className="mt-4 space-y-3">
              {/* Strength Bar */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">Password strength:</span>
                  <span
                    className={`font-medium ${
                      calculatePasswordStrength(securityData.password).color ===
                      "red"
                        ? "text-red-600"
                        : calculatePasswordStrength(securityData.password)
                            .color === "orange"
                        ? "text-orange-600"
                        : calculatePasswordStrength(securityData.password)
                            .color === "yellow"
                        ? "text-yellow-600"
                        : calculatePasswordStrength(securityData.password)
                            .color === "lime"
                        ? "text-lime-600"
                        : calculatePasswordStrength(securityData.password)
                            .color === "green"
                        ? "text-green-600"
                        : "text-emerald-600"
                    }`}
                  >
                    {calculatePasswordStrength(securityData.password).label}
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      calculatePasswordStrength(securityData.password).color ===
                      "red"
                        ? "bg-red-500"
                        : calculatePasswordStrength(securityData.password)
                            .color === "orange"
                        ? "bg-orange-500"
                        : calculatePasswordStrength(securityData.password)
                            .color === "yellow"
                        ? "bg-yellow-500"
                        : calculatePasswordStrength(securityData.password)
                            .color === "lime"
                        ? "bg-lime-500"
                        : calculatePasswordStrength(securityData.password)
                            .color === "green"
                        ? "bg-green-500"
                        : "bg-emerald-500"
                    }`}
                    style={{
                      width: `${
                        (calculatePasswordStrength(securityData.password)
                          .score /
                          7) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>

              {/* Requirements Checklist */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div
                  className={`flex items-center gap-2 ${
                    calculatePasswordStrength(securityData.password).checks
                      .length
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      calculatePasswordStrength(securityData.password).checks
                        .length
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  At least 8 characters
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    calculatePasswordStrength(securityData.password).checks
                      .lowercase
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      calculatePasswordStrength(securityData.password).checks
                        .lowercase
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Lowercase letter
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    calculatePasswordStrength(securityData.password).checks
                      .uppercase
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      calculatePasswordStrength(securityData.password).checks
                        .uppercase
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Uppercase letter
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    calculatePasswordStrength(securityData.password).checks
                      .numbers
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      calculatePasswordStrength(securityData.password).checks
                        .numbers
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Number
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    calculatePasswordStrength(securityData.password).checks
                      .symbols
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      calculatePasswordStrength(securityData.password).checks
                        .symbols
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  Special character
                </div>
                <div
                  className={`flex items-center gap-2 ${
                    securityData.password.length >= 12
                      ? "text-emerald-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      securityData.password.length >= 12
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    }`}
                  />
                  12+ characters
                </div>
              </div>
            </div>
          )}

          {securityData.password_confirmation &&
            securityData.password !== securityData.password_confirmation && (
              <div className="mt-2 text-xs text-red-500">
                Passwords don't match
              </div>
            )}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handlePasswordUpdate}
            disabled={
              isChangingPassword ||
              !securityData.old_password ||
              !securityData.password ||
              !securityData.password_confirmation
            }
            className={`px-8 py-3 font-medium text-white rounded-lg transition-colors ${
              isChangingPassword ||
              !securityData.old_password ||
              !securityData.password ||
              !securityData.password_confirmation
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-emerald-500 hover:bg-emerald-600"
            }`}
          >
            {isChangingPassword ? "Updating password..." : "Update password"}
          </button>
        </div>
      </div>
    </motion.section>
  );

  const renderNotificationSection = () => (
    <motion.section
      {...cardMotion}
      className="overflow-hidden relative p-6 bg-white rounded-2xl ring-1 ring-gray-100 shadow-xl"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Notification preferences
        </h2>
        <Edit className="w-5 h-5 text-gray-400" />
      </div>

      <div className="space-y-6">
        <div>
          <p className="mb-3 text-gray-700">
            1. Would you like to receive updates on new financial offers?
          </p>
          <div className="flex gap-6">
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="radio"
                name="financialOffers"
                value="yes"
                checked={notificationPrefs.financialOffers === "yes"}
                onChange={(e) =>
                  handleNotificationUpdate("financialOffers", e.target.value)
                }
                className="w-4 h-4 accent-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">Yes</span>
            </label>
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="radio"
                name="financialOffers"
                value="no"
                checked={notificationPrefs.financialOffers === "no"}
                onChange={(e) =>
                  handleNotificationUpdate("financialOffers", e.target.value)
                }
                className="w-4 h-4 accent-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">No</span>
            </label>
          </div>
        </div>

        <div>
          <p className="mb-3 text-gray-700">
            2. Would you like to receive promotional deals?
          </p>
          <div className="flex gap-6">
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="radio"
                name="promotionalDeals"
                value="yes"
                checked={notificationPrefs.promotionalDeals === "yes"}
                onChange={(e) =>
                  handleNotificationUpdate("promotionalDeals", e.target.value)
                }
                className="w-4 h-4 accent-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">Yes</span>
            </label>
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="radio"
                name="promotionalDeals"
                value="no"
                checked={notificationPrefs.promotionalDeals === "no"}
                onChange={(e) =>
                  handleNotificationUpdate("promotionalDeals", e.target.value)
                }
                className="w-4 h-4 accent-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">No</span>
            </label>
          </div>
        </div>

        <div>
          <p className="mb-3 text-gray-700">
            3. Select your preferred method of notifications
          </p>
          <div className="flex gap-6">
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="radio"
                name="method"
                value="email"
                checked={notificationPrefs.method === "email"}
                onChange={(e) =>
                  handleNotificationUpdate("method", e.target.value)
                }
                className="w-4 h-4 accent-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">Email</span>
            </label>
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="radio"
                name="method"
                value="sms"
                checked={notificationPrefs.method === "sms"}
                onChange={(e) =>
                  handleNotificationUpdate("method", e.target.value)
                }
                className="w-4 h-4 accent-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-gray-700">SMS</span>
            </label>
          </div>
        </div>
      </div>
    </motion.section>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "profile":
        return renderProfileSection();
      case "security":
        return renderSecuritySection();
      case "notification":
        return renderNotificationSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="px-4 pt-20 pb-4 min-h-screen bg-gray-50 sm:pt-24 sm:pb-6 sm:px-6 lg:pt-28 lg:pb-8 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <motion.button
            onClick={() => navigate(-1)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 mb-4 text-gray-600 transition-colors hover:text-emerald-600"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </motion.button>
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage all your account settings</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="relative p-4 bg-white rounded-2xl ring-1 ring-gray-100 shadow-xl">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
              <nav className="space-y-2 mt-1.5">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-colors ${
                        isActive
                          ? "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`grid place-items-center w-8 h-8 rounded-lg ${
                          isActive
                            ? "text-emerald-700 bg-emerald-100"
                            : "text-gray-600 bg-gray-100"
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                      </span>
                      <span className="font-medium">{item.label}</span>
                      <ChevronRight
                        className={`ml-auto w-4 h-4 ${
                          isActive ? "text-emerald-600" : "text-gray-400"
                        }`}
                      />
                      {isActive && (
                        <span className="absolute right-0 inset-y-2 w-1 bg-emerald-500 rounded-full" />
                      )}
                    </motion.button>
                  );
                })}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-3"
          >
            {renderContent()}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
